"use client";

import useOpenMaps from "@/components/hooks/use-open-maps";
import Container from "@/components/layout/Container";
import Flex from "@/components/layout/Flex";
import Section from "@/components/layout/Section";
import Headline from "@/components/project/Headline";
import Typography from "@/components/shared/Typography";
import Visibility from "@/components/shared/Visibility";
import Button from "@/components/ui/Button";
import Card from "@/components/ui/Card";
import { ORG } from "@/content/_site.config";
import { HREF_EMAIL, HREF_PHONE_NUMBER } from "@/lib/consts";
import { breakpointClasses, cn, splitBreakpointProps } from "@/lib/utils";
import { HubSpotForm } from "./parts";
import type { ContactUsSectionProps } from "./types";

function ContactUsSection({
  headline,
  header,
  description = [],
  className,
  ...props
}: ContactUsSectionProps) {
  const openMaps = useOpenMaps();
  const { bp, native } = splitBreakpointProps(props);
  const classes = breakpointClasses(bp);

  const Address = `${ORG.address.streetAddress}, ${ORG.address.addressLocality}, ${ORG.address.addressCountry} ${ORG.address.postalCode}`;

  const DetailDescription = [
    "Our office administrator is available to answer your questions or direct you to the appropriate consultant. They are available via phone from 9AM to 4PM CDT Monday - Thursday",
    "We will respond to emails by the end next business day. Please do not hesitate to contact us.",
  ];

  return (
    <Flex.Column asChild md="md:flex-row">
      <Container
        asChild
        maxWidth="max-w-md"
        md="md:max-w-3xl"
        lg="lg:max-w-6xl"
      >
        <Section id="contact-us" className={cn(className, classes)} {...native}>
          <Flex.Column fill>
            <Headline>{headline}</Headline>

            <Visibility shouldShow={!!header}>
              <Typography.H3>{header}</Typography.H3>
            </Visibility>

            <Typography.Description description={description} />

            <Button.Link
              variant="link"
              href={HREF_PHONE_NUMBER}
              aria-label="Press to call us"
            >
              <Typography.Large>{ORG.telephone}</Typography.Large>
            </Button.Link>
            <Button.Link
              variant="link"
              href={HREF_EMAIL}
              aria-label="Press to email us"
            >
              <Typography.Large>{ORG.email}</Typography.Large>
            </Button.Link>
            <Button
              variant="link"
              onClick={openMaps}
              className="text-left"
              aria-label="Press to open maps"
            >
              <Typography.Large>{Address}</Typography.Large>
            </Button>
          </Flex.Column>

          <Card fill>
            <HubSpotForm />

            <Typography.Description
              description={DetailDescription}
              paragraphClassName="~text-xs/base font-body font-medium leading-none"
            />
          </Card>
        </Section>
      </Container>
    </Flex.Column>
  );
}

export default ContactUsSection;
